/** @jsxImportSource @emotion/react */
import React, { useReducer } from 'react'
import { css } from '@emotion/react'
import { TextInput } from '../../../../components/ts-components/TextInput'
import { TextAreaInput } from '../../../../components/ts-components/TextAreaInput'
import { SelectInput } from '../../../../components/ts-components/SelectInput'
import { NumberInput } from '../../../../components/ts-components/NumberInput'
import DatePicker from '../../../../components/ts-components/DatePicker'
import { DateOfBirthInput } from '../../../../components/ts-components/DateOfBirthInput'
// import ModernPhoneInput from '../../../../components/ts-components/ModernPhoneInput'
import { CountrySelectInput } from '../../../../components/ts-components/CountrySelectInput'
import { PlayerPositionSelect } from '../../../../components/ts-components/PlayerPositionSelect'
import { Checkbox } from '../../../../components/ts-components/Checkbox'
import Button from '../../../../components/ts-components/Button'
import { FormGroup, FormItem } from '../../../../types/CustomForm'
import {
  filterEnabledGroups,
  sortGroupsByIndex,
  sortItemsByIndex,
  mapFormItemTypeToKind,
  extractOptions,
  extractNumberRange,
  extractMaxChar,
  formatFieldLabel,
} from '../../../../utils/customFormHelpers'
import {
  Container,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  QuestionsGrid,
  QuestionContainer,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateMessage,
  ButtonContainer,
} from './styles'
import { Dayjs } from 'dayjs'
import {
  DISABLE_FORM_INTERACTION_IN_PREVIEW,
  HIDE_BUTTONS_IN_PREVIEW,
} from '../../constants'
import RichTextRenderer from 'src/components/RichTextRenderer'
import PhoneInput from 'src/components/ts-components/PhoneInput'

interface DynamicFormPreviewProps {
  /** Array of form groups (sections) to render */
  groups: FormGroup[]
  /** Sport ID for player position selection */
  sportId?: string
  /** Handler for back button click */
  onBack?: () => void
  /** Handler for next button click */
  onNext?: () => void
  /** Loading state for buttons */
  loading?: boolean
  /** Custom text for back button */
  backButtonText?: string
  /** Custom text for next button */
  nextButtonText?: string
}

// Answer state types
type AnswerState = Record<
  string /* item.id */,
  string | number | boolean | Dayjs | null
>

type AnswerAction =
  | { type: 'SET'; id: string; value: string | number | boolean | Dayjs | null }
  | { type: 'RESET' }

/**
 * Reducer for managing form answer state
 */
function answersReducer(state: AnswerState, action: AnswerAction): AnswerState {
  switch (action.type) {
    case 'SET':
      return { ...state, [action.id]: action.value }
    case 'RESET':
      return {}
    default:
      return state
  }
}

// Content section component with read more functionality
const ContentSection: React.FC<{ content: string }> = ({ content }) => {
  return (
    <div css={SectionContainer}>
      {/* Top separator */}
      <div
        css={css`
          height: 1px;
          background: #33363b;
          margin: 0 0;
        `}
      />
      {/* Content */}
      <RichTextRenderer content={content} />
      {/* Bottom separator */}
      <div
        css={css`
          height: 1px;
          background: #33363b;
          margin: 0 0;
        `}
      />
    </div>
  )
}

/**
 * DynamicFormPreview component renders custom form sections inline
 * Displays enabled sections with their enabled questions in a 2-column grid
 * Now supports all field types with real user interaction, including player position selection
 *
 * @param props - Component props containing form groups and sport ID
 * @returns JSX element containing the rendered form preview
 *
 * @example
 * ```tsx
 * <DynamicFormPreview
 *   groups={selectedForm.groups}
 *   sportId={registrationData.sport_id}
 *   onBack={() => console.log('Back clicked')}
 *   onNext={() => console.log('Next clicked')}
 *   loading={false}
 * />
 * ```
 */
const DynamicFormPreview: React.FC<DynamicFormPreviewProps> = ({
  groups,
  sportId,
  onBack,
  onNext,
  loading = false,
  backButtonText = 'Back',
  nextButtonText = 'Next',
}) => {
  // State management for form answers
  const [answers, dispatchAnswer] = useReducer(answersReducer, {})

  // Filter and sort groups and their items
  const enabledGroups = filterEnabledGroups(groups)
  const sortedGroups = sortGroupsByIndex(enabledGroups).map(sortItemsByIndex)

  /**
   * 🔄 getFieldLabel: Gets the appropriate label for a field, including character limits for text-based fields
   */
  const getFieldLabel = (item: FormItem, kind: string): string => {
    // Show character limits for text-based fields that support maxChar
    const textBasedFields = ['text', 'email', 'url', 'textarea']

    if (textBasedFields.includes(kind)) {
      return formatFieldLabel(item)
    }

    // For other field types, use the standard label format
    return `${item.display_label || item.name}${
      item.is_required === 1 ? ' *' : ''
    }`
  }

  /**
   * Renders the appropriate input field based on form item type
   */
  const renderField = (item: FormItem) => {
    const kind = mapFormItemTypeToKind(item.form_item_type.id)
    const commonProps = {
      name: item.id,
      fieldErrors: [],
      customError: {},
      label: getFieldLabel(item, kind),
      disabled: DISABLE_FORM_INTERACTION_IN_PREVIEW,
    }

    switch (kind) {
      case 'text': {
        const maxChar = extractMaxChar(item)
        return (
          <TextInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.name}`}
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'number': {
        const { min, max } = extractNumberRange(item)
        const currentValue = answers[item.id]
        const stringValue =
          typeof currentValue === 'string' || typeof currentValue === 'number'
            ? String(currentValue)
            : ''
        return (
          <NumberInput
            {...commonProps}
            value={stringValue}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            min={min}
            max={max}
            placeholder={`Enter ${item.name}`}
          />
        )
      }

      case 'player_number': {
        const currentValue = answers[item.id]
        const stringValue =
          typeof currentValue === 'string' || typeof currentValue === 'number'
            ? String(currentValue)
            : ''
        return (
          <NumberInput
            {...commonProps}
            value={stringValue}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.name}`}
          />
        )
      }

      case 'dropdown': {
        const options = extractOptions(item)
        return (
          <SelectInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            options={options}
            placeholder={`Select ${item.name}`}
            disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
          />
        )
      }

      case 'checkbox':
        return (
          <div
            css={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              width: '100%',
            }}
          >
            <Checkbox
              label={commonProps.label}
              name={commonProps.name}
              value={Boolean(answers[item.id])}
              onChange={(e) =>
                dispatchAnswer({
                  type: 'SET',
                  id: item.id,
                  value: e.target.checked,
                })
              }
              disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
              hint={item?.description ?? ''}
              fieldErrors={[]}
              customError={{}}
              checkBoxStyle={css`
                width: auto;
                flex: 0 0 auto;
              `}
            />
          </div>
        )

      case 'textarea': {
        const maxChar = extractMaxChar(item)
        return (
          <TextAreaInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.name}...`}
            rows={3}
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'date':
        return (
          <DatePicker
            label={commonProps.label}
            value={answers[item.id] as Dayjs | null}
            onChange={(date) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: date })
            }
            disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
          />
        )

      case 'birthdate':
        return (
          <DateOfBirthInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
          />
        )

      // case 'phone':
      //   return (
      //     <ModernPhoneInput
      //       label={commonProps.label}
      //       value={String(answers[item.id] ?? '')}
      //       onChange={(v) =>
      //         dispatchAnswer({ type: 'SET', id: item.id, value: v })
      //       }
      //       disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
      //     />
      //   )

      case 'phone':
        return (
          <PhoneInput
            label={commonProps.label}
            value={String(answers[item.id] ?? '')}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
          />
        )

      case 'email': {
        const maxChar = extractMaxChar(item)
        return (
          <TextInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.display_label || item.name}`}
            type='email'
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'url': {
        const maxChar = extractMaxChar(item)
        return (
          <TextInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            setValue={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
            }
            placeholder={`Enter ${item.name}`}
            type='url'
            maxLength={maxChar || undefined}
          />
        )
      }

      case 'country':
        return (
          <CountrySelectInput
            {...commonProps}
            value={String(answers[item.id] ?? '')}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v || null })
            }
            disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
          />
        )

      case 'playerPosition':
        return (
          <PlayerPositionSelect
            {...commonProps}
            sportId={sportId}
            value={answers[item.id] as number | undefined}
            onChange={(v) =>
              dispatchAnswer({ type: 'SET', id: item.id, value: v ?? null })
            }
            disabled={DISABLE_FORM_INTERACTION_IN_PREVIEW}
          />
        )

      default:
        return null
    }
  }

  // Show empty state if no enabled groups
  if (sortedGroups.length === 0) {
    return (
      <div css={Container}>
        <div css={EmptyStateContainer}>
          <div css={EmptyStateTitle}>No Form Content</div>
          <div css={EmptyStateMessage}>
            This form has no enabled sections or questions to display.
          </div>
        </div>

        {/* Buttons for empty state */}
        {!HIDE_BUTTONS_IN_PREVIEW && (onBack || onNext) && (
          <div css={ButtonContainer}>
            {onBack && (
              <Button
                variant='secondary'
                text={backButtonText}
                disabled={loading}
                onClick={onBack}
              />
            )}
            {onNext && (
              <Button
                variant='primary'
                text={nextButtonText}
                disabled={loading}
                onClick={onNext}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div css={Container}>
      {sortedGroups.map((group) => {
        const isContentGroup = (group as any).is_content
        const content = (group as any).content

        if (isContentGroup && content) {
          // Render content section without header
          return <ContentSection key={group.id} content={content} />
        }

        // Render regular section with questions
        return (
          <div key={group.id} css={SectionContainer}>
            {/* Section Header */}
            <div css={SectionHeader}>
              <div css={SectionTitle}>{group.name}</div>
              {group.description && (
                <div css={SectionSubtitle}>{group.description}</div>
              )}
            </div>

            {/* Questions Grid */}
            <div css={QuestionsGrid}>
              {group.items.map((item) => {
                const fieldKind = mapFormItemTypeToKind(item.form_item_type.id)
                const isWideField = ['textarea', 'checkbox'].includes(fieldKind)

                return (
                  <div
                    key={item.id}
                    css={[
                      QuestionContainer,
                      isWideField && {
                        gridColumn: '1 / -1', // span across both columns
                      },
                    ]}
                  >
                    {renderField(item)}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Bottom Action Buttons */}
      {!HIDE_BUTTONS_IN_PREVIEW && (
        <div css={ButtonContainer}>
          <Button
            variant='secondary'
            text={backButtonText}
            disabled={loading}
            onClick={onBack || (() => {})}
          />
          <Button
            variant='primary'
            text={nextButtonText}
            disabled={loading}
            onClick={onNext || (() => {})}
          />
        </div>
      )}
    </div>
  )
}

export default DynamicFormPreview
